import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
import { BiFilterAlt } from 'react-icons/bi';
import { MdArrowBack } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { useProducts } from '../context/ProductsContext';
import './Products.css';

const Products = () => {
    const { category } = useParams();
    const navigate = useNavigate();
    const { addToWishlist, removeFromWishlist, isInWishlist, addToCart } = useAuth();
    const { getProductsByCategory, getAllProducts, loading: productsLoading } = useProducts();
    const [sortBy, setSortBy] = useState('featured');
    const [showFilters, setShowFilters] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedMaterial, setSelectedMaterial] = useState('');
    const [availableSizes, setAvailableSizes] = useState([]);
    const [availableMaterials, setAvailableMaterials] = useState([]);

    useEffect(() => {
        // Load products from JSON based on category
        const loadedProducts = category 
            ? getProductsByCategory(category)
            : getAllProducts();
        
        // Transform to display format
        const transformedProducts = loadedProducts.map(product => {
            // Use originalPrice as base and discountedPrice from JSON
            const originalPrice = parseInt(product.originalPrice || 0);
            const discountedPrice = product.discountedPrice && product.discountedPrice !== ''
                ? parseInt(product.discountedPrice)
                : originalPrice;
            const effectivePrice = product.discountedPrice && product.discountedPrice !== ''
                ? discountedPrice
                : originalPrice;
            const discount = parseInt(product.discount) || 0;
            const productImages = Array.isArray(product.images) ? product.images : [];
            const primaryImage =
                productImages.find((img) => !/\.heic$/i.test(img)) ||
                productImages[0] ||
                '/No-Image-Placeholder.svg';
            
            return {
                // Keep all original data for navigation
                ...product,
                id: product.productId,
                name: product.productTitle,
                originalPrice: originalPrice,
                discountedPrice: discountedPrice,
                effectivePrice: effectivePrice,
                category: product.category,
                image: primaryImage,
                discount: discount,
                inStock: product.inStock
            };
        });
        
        setProducts(transformedProducts);
        
        // Extract unique sizes and materials
        const sizesSet = new Set();
        const materialsSet = new Set();
        
        transformedProducts.forEach(product => {
            // Add sizes
            if (product.availableSize && Array.isArray(product.availableSize)) {
                product.availableSize.forEach(size => sizesSet.add(size));
            }
            // Add materials
            if (product.material) {
                materialsSet.add(product.material);
            }
        });
        
        setAvailableSizes(Array.from(sizesSet).sort());
        setAvailableMaterials(Array.from(materialsSet).sort());
    }, [category, getProductsByCategory, getAllProducts]);

    // Apply filters
    const filteredProducts = products.filter(product => {
        // Size filter
        if (selectedSize && product.availableSize) {
            if (!product.availableSize.includes(selectedSize)) {
                return false;
            }
        }
        
        // Material filter
        if (selectedMaterial && product.material !== selectedMaterial) {
            return false;
        }
        
        return true;
    });

    const sortedProducts = [...filteredProducts].sort((a, b) => {
        switch (sortBy) {
            case 'price-low':
                return a.effectivePrice - b.effectivePrice;
            case 'price-high':
                return b.effectivePrice - a.effectivePrice;
            case 'discount':
                return b.discount - a.discount;
            default:
                return 0;
        }
    });

    const toggleWishlist = (product) => {
        if (isInWishlist(product.id)) {
            removeFromWishlist(product.id);
        } else {
            // Format product data to match wishlist structure
            const wishlistItem = {
                id: product.id,
                title: product.name,
                name: product.name,
                originalPrice: product.originalPrice,
                discountedPrice: product.discountedPrice,
                discount: product.discount,
                image: product.image,
                inStock: true,
                category: product.category
            };
            addToWishlist(wishlistItem);
        }
    };

    const getCategoryTitle = () => {
        if (!category) return 'All Products';
        return category.charAt(0).toUpperCase() + category.slice(1);
    };

    return (
        <div className="products-page">
            <button className="back-btn" onClick={() => navigate(-1)}>
                <MdArrowBack size={20} />
                Back
            </button>

            <div className="products-header">
                <h1>{getCategoryTitle()}</h1>
                <p>{sortedProducts.length} products</p>
            </div>

            <div className="products-controls">
                <button className="filter-btn" onClick={() => setShowFilters(!showFilters)}>
                    <BiFilterAlt size={20} />
                    Filters
                </button>
                <select 
                    className="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="discount">Discount</option>
                </select>
            </div>

            {showFilters && (
                <div className="filters-panel">
                    <div className="filter-group">
                        <label>Size</label>
                        <select 
                            value={selectedSize} 
                            onChange={(e) => setSelectedSize(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Sizes</option>
                            {availableSizes.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Material</label>
                        <select 
                            value={selectedMaterial} 
                            onChange={(e) => setSelectedMaterial(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Materials</option>
                            {availableMaterials.map(material => (
                                <option key={material} value={material}>{material}</option>
                            ))}
                        </select>
                    </div>

                    {(selectedSize || selectedMaterial) && (
                        <button 
                            className="clear-filters-btn"
                            onClick={() => {
                                setSelectedSize('');
                                setSelectedMaterial('');
                            }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
            )}

            <div className="products-grid">
                {sortedProducts.map((product) => {
                    const inWishlist = isInWishlist(product.id);

                    return (
                        <div key={product.id} className="product-card">
                            {product.discount > 0 && (
                                <span className="discount-badge">{product.discount}%</span>
                            )}
                            <button 
                                className="wishlist-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleWishlist(product);
                                }}
                            >
                                {inWishlist ? (
                                    <AiFillHeart size={24} color="#e63946" />
                                ) : (
                                    <AiOutlineHeart size={24} />
                                )}
                            </button>
                            
                            <div 
                                className="product-clickable"
                                onClick={() => navigate(`/product/${product.id}`, { state: { product } })}
                                style={{ cursor: 'pointer' }}
                            >
                                <img 
                                    src={product.image} 
                                    alt={product.name}
                                    className="product-image"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = '/No-Image-Placeholder.svg';
                                    }}
                                />
                                
                                <div className="product-info">
                                    <h3>{product.name}</h3>
                                    <div className="product-price">
                                        {product.discount > 0 ? (
                                            <>
                                                <span className="original-price">₹{product.originalPrice}</span>
                                                <span className="current-price">₹{product.discountedPrice}</span>
                                            </>
                                        ) : (
                                            <span className="current-price">₹{product.originalPrice}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <button 
                                className="add-to-cart-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const cartItem = {
                                        id: product.id,
                                        title: product.name,
                                        name: product.name,
                                        price: product.discountedPrice,
                                        image: product.image,
                                        quantity: 1,
                                        inStock: product.inStock
                                    };
                                    addToCart(cartItem);
                                    alert('Added to cart!');
                                }}
                            >
                                Add to Cart
                            </button>
                        </div>
                    );
                })}
            </div>

            {sortedProducts.length === 0 && !productsLoading && (
                <div className="no-products">
                    <p>No products found in this category.</p>
                    <button onClick={() => navigate('/')}>Browse All Products</button>
                </div>
            )}
        </div>
    );
};

export default Products;
